import Popover from '@mui/material/Popover';
import type { KeyboardEvent, MouseEvent, ReactNode, RefObject } from 'react';
import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';

type PopoverOrigin = {
  vertical: 'top' | 'bottom' | 'center' | number;
  horizontal: 'left' | 'right' | 'center' | number;
};

type HoverClickPopoutProps = {
  children: ReactNode;
  content?: ReactNode;
  onOpen?:
    | ((event: MouseEvent<HTMLDivElement> | KeyboardEvent<HTMLDivElement>) => void)
    | (() => void);
  onClose?: () => void;
  isOpen?: boolean;
  anchorEl?: HTMLElement | null;
  width?: string;
  renderContent?: (closePopout: () => void) => ReactNode;
  disableInteraction?: boolean;
  anchorOrigin?: PopoverOrigin;
  transformOrigin?: PopoverOrigin;
  fullWidth?: boolean;
  /** When false, the panel opens only on click / keyboard — not hover. */
  openOnHover?: boolean;
  /** Gap between the trigger and the panel (px). */
  offset?: number;
  /**
   * Optional shared element used only for positioning (e.g. nav footer),
   * so multiple popovers share one baseline while keeping their own triggers.
   */
  positionAnchorRef?: RefObject<HTMLElement | null>;
  /**
   * Pin the panel’s bottom-left to the app content pane’s bottom-left
   * (`[data-app-content-pane]`), so Help / Inbox / Account share one baseline.
   */
  alignToContentPaneBottom?: boolean;
};

const CONTENT_PANE_SELECTOR = '[data-app-content-pane]';

export default function HoverClickPopout({
  children,
  content,
  onOpen,
  onClose,
  isOpen: externalIsOpen,
  anchorEl: externalAnchorEl,
  width = '300px',
  renderContent,
  disableInteraction = false,
  anchorOrigin = { vertical: 'bottom', horizontal: 'right' },
  transformOrigin = { vertical: 'bottom', horizontal: 'left' },
  fullWidth = false,
  openOnHover = true,
  offset = 4,
  positionAnchorRef,
  alignToContentPaneBottom = false,
}: HoverClickPopoutProps) {
  const isControlled = useMemo(
    () => externalIsOpen !== undefined && externalAnchorEl !== undefined,
    [externalIsOpen, externalAnchorEl],
  );

  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const [hoverAnchorEl, setHoverAnchorEl] = useState<HTMLElement | null>(null);
  const [anchorPosition, setAnchorPosition] = useState<{ top: number; left: number } | null>(
    null,
  );
  const triggerRef = useRef<HTMLDivElement>(null);

  const isClickOpen = isControlled ? Boolean(externalIsOpen) : internalIsOpen;
  const isHoverOpen = openOnHover && Boolean(hoverAnchorEl) && !isClickOpen;
  const isPopoverOpen = isClickOpen || isHoverOpen;

  const fallbackAnchorEl = isControlled
    ? externalAnchorEl
    : isClickOpen
      ? triggerRef.current
      : hoverAnchorEl;

  const activeAnchorEl = isPopoverOpen
    ? (positionAnchorRef?.current ?? fallbackAnchorEl ?? null)
    : null;

  const syncContentPanePosition = useCallback(() => {
    const pane = document.querySelector(CONTENT_PANE_SELECTOR);
    if (!(pane instanceof HTMLElement)) {
      setAnchorPosition(null);
      return;
    }
    const rect = pane.getBoundingClientRect();
    setAnchorPosition({
      top: rect.bottom,
      left: rect.left + offset,
    });
  }, [offset]);

  useLayoutEffect(() => {
    if (!alignToContentPaneBottom || !isPopoverOpen) {
      setAnchorPosition(null);
      return;
    }

    syncContentPanePosition();
    window.addEventListener('resize', syncContentPanePosition);
    window.addEventListener('scroll', syncContentPanePosition, true);
    return () => {
      window.removeEventListener('resize', syncContentPanePosition);
      window.removeEventListener('scroll', syncContentPanePosition, true);
    };
  }, [alignToContentPaneBottom, isPopoverOpen, syncContentPanePosition]);

  const closePopoverHandler = useCallback(() => {
    setHoverAnchorEl(null);

    if (!isControlled) {
      setInternalIsOpen(false);
    }

    onClose?.();
  }, [isControlled, onClose]);

  const getContentElement = useCallback(() => {
    return renderContent ? renderContent(closePopoverHandler) : content;
  }, [renderContent, content, closePopoverHandler]);

  const handleClick = useCallback(
    (event: MouseEvent<HTMLDivElement>) => {
      setHoverAnchorEl(null);

      if (!isControlled) {
        setInternalIsOpen((prevState) => !prevState);
      }

      if (!isClickOpen) {
        onOpen?.(event);
      } else {
        onClose?.();
      }

      event.preventDefault();
      event.stopPropagation();
    },
    [isControlled, onOpen, onClose, isClickOpen],
  );

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key === ' ') {
        event.preventDefault();
      }

      if (event.key === 'Enter' || event.key === ' ') {
        setHoverAnchorEl(null);

        if (!isControlled) {
          setInternalIsOpen((prevState) => !prevState);
        }

        if (!isClickOpen) {
          onOpen?.(event);
        } else {
          onClose?.();
        }
      } else if (event.key === 'Escape' && isClickOpen) {
        closePopoverHandler();
      }
    },
    [isControlled, isClickOpen, onOpen, onClose, closePopoverHandler],
  );

  const handlePopoverOpen = useCallback(
    (event: MouseEvent<HTMLElement>) => {
      if (!openOnHover || isClickOpen) return;
      setHoverAnchorEl(event.currentTarget);
    },
    [openOnHover, isClickOpen],
  );

  const handlePopoverClose = useCallback(() => {
    setHoverAnchorEl(null);
  }, []);

  const useContentPaneAnchor = alignToContentPaneBottom && !!anchorPosition;

  const commonPopoverProps = useMemo(
    () => ({
      anchorOrigin: useContentPaneAnchor
        ? ({ vertical: 'bottom', horizontal: 'left' } as const)
        : anchorOrigin,
      transformOrigin: useContentPaneAnchor
        ? ({ vertical: 'bottom', horizontal: 'left' } as const)
        : transformOrigin,
      disablePortal: false,
      elevation: 0,
      marginThreshold: null,
      slotProps: {
        paper: {
          style: {
            width,
            background: 'transparent',
            boxShadow: 'none',
            overflow: 'visible',
            marginLeft: useContentPaneAnchor
              ? 0
              : transformOrigin.horizontal === 'left'
                ? offset
                : transformOrigin.horizontal === 'right'
                  ? -offset
                  : 0,
          },
          className: 'bg-transparent! shadow-none! overflow-visible!',
        },
      },
    }),
    [anchorOrigin, transformOrigin, width, offset, useContentPaneAnchor],
  );

  return (
    <div
      className={fullWidth ? 'flex w-full' : 'inline-flex justify-center'}
      data-testid="hover-click-wrapper"
    >
      <div
        ref={triggerRef}
        role="button"
        tabIndex={0}
        aria-haspopup="true"
        aria-expanded={isClickOpen || isHoverOpen}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        onMouseEnter={openOnHover ? handlePopoverOpen : undefined}
        onMouseLeave={openOnHover ? handlePopoverClose : undefined}
        className={fullWidth ? 'flex w-full cursor-pointer' : 'inline-flex cursor-pointer'}
        data-testid="hover-click-trigger"
      >
        {children}
      </div>

      <Popover
        id="hover-click-popover"
        sx={{
          pointerEvents: isClickOpen ? 'auto' : 'none',
          '& .MuiPaper-root': {
            pointerEvents: disableInteraction ? 'none' : 'auto',
            width,
          },
        }}
        open={isPopoverOpen}
        anchorReference={useContentPaneAnchor ? 'anchorPosition' : 'anchorEl'}
        anchorPosition={useContentPaneAnchor ? anchorPosition : undefined}
        anchorEl={useContentPaneAnchor ? null : activeAnchorEl}
        onClose={closePopoverHandler}
        disableRestoreFocus={!isClickOpen}
        {...commonPopoverProps}
      >
        <div
          onMouseEnter={() => {
            if (openOnHover && !disableInteraction && !isClickOpen) {
              setHoverAnchorEl(triggerRef.current);
            }
          }}
          onMouseLeave={() => {
            if (openOnHover && !isClickOpen) {
              handlePopoverClose();
            }
          }}
        >
          {getContentElement()}
        </div>
      </Popover>
    </div>
  );
}
